/* voyado-recommendations-core.js
 * Shared Voyado recommendation rendering + slider behaviour.
 * Exposed as window.VoyadoRec so both the ES-module consumers
 * (voyado-slider.js, voyado-product-recommendations.js) can use it —
 * ES modules can't statically import Shopify's hashed asset URLs, so we
 * follow the same global pattern as window.dragScroll / window.swiperNav.
 */
(function () {
  const PLUS_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
  const PREV_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="15 18 9 12 15 6"/></svg>`;
  const NEXT_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="9 18 15 12 9 6"/></svg>`;
  const NEEDS_PRODUCT = new Set(["ALTERNATIVES", "STYLE_WITH", "MORE_FROM_SERIES", "UPSELL", "ADD_TO_CART_RECS"]);

  function fmt(v) {
    if (v == null) return "";
    const n =
      typeof v === "object" ? Number(v.min ?? v.amount ?? 0) : Number(v);
    return Math.round(n).toLocaleString("nb-NO") + " kr";
  }
  function esc(s) {
    return String(s).replace(
      /[&<>"]/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
    );
  }
  function escA(s) {
    return String(s).replace(/["']/g, (c) => (c === '"' ? "&quot;" : "&#39;"));
  }
  function safeUrl(u) {
    const s = String(u ?? "");
    return s.startsWith("/") ||
      s.startsWith("https://") ||
      s.startsWith("http://")
      ? s
      : "#";
  }

  // Slider/recommendation items are ~416px wide at most (1/4 viewport
  // desktop), so 800px covers 2x retina without the master-resolution file.
  function resizedUrl(url, width = 800) {
    if (!url || url === "#") return url;
    return url.includes("?") ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

  function renderBadges(p, disc, sale) {
    const items = [];
    let hasSoldout = false;
    if (sale && disc)
      items.push(`<span class="product-card__badge">-${disc}%</span>`);
    for (const b of p.badges?.primary ?? []) {
      if (b.theme === "DISCOUNT" || !b.label) continue;
      if (b.attribute === "stock_number") {
        if (Number(b.label) === 0) {
          items.push(`<span class="product-card__badge">Utsolgt</span>`);
          hasSoldout = true;
        }
        continue;
      }
      items.push(`<span class="product-card__badge">${esc(String(b.label))}</span>`);
    }
    for (const b of p.badges?.secondary ?? []) {
      if (!b.label) continue;
      items.push(`<span class="product-card__badge product-card__badge--theme2">${esc(String(b.label))}</span>`);
    }
    if (!p.inStock && !hasSoldout)
      items.push(
        `<span class="product-card__badge product-card__badge--soldout">Utsolgt</span>`
      );
    return items.length
      ? `<div class="product-card__badges">${items.join("")}</div>`
      : "";
  }

  function renderCard(p, ticket, i) {
    const url = safeUrl(p.link);
    const title = p.title ?? "";
    const vendor = p.brand ?? "";
    const sp = p.sellingPrice?.min ?? null;
    const lp = p.listPrice?.min ?? null;
    const atcVariant = p.variants?.find((v) => v.inStock) ?? p.variants?.[0];
    const atcVariantTicket = atcVariant?.ticket ?? "";
    const sale = lp !== null && sp !== null && Number(lp) > Number(sp);
    const imageInfo = p.imageInfo ?? {};
    const effect = imageInfo.effect ?? "NONE";
    const images = imageInfo.images ?? [];
    const rawIdle = images[0]?.sources?.[0]?.url ?? null;
    const img = rawIdle
      ? resizedUrl(safeUrl(rawIdle.startsWith("http") ? rawIdle : `https:${rawIdle}`))
      : null;
    const isSwap = effect === "SWAP";
    const isGallery = effect === "GALLERY";
    const rawHover = isSwap ? (images[1]?.sources?.[0]?.url ?? null) : null;
    const imgHover = rawHover
      ? resizedUrl(safeUrl(
          rawHover.startsWith("http") ? rawHover : `https:${rawHover}`
        ))
      : null;
    const galleryUrls =
      isGallery && images.length > 1
        ? images
            .map((m) => {
              const r = m?.sources?.[0]?.url ?? null;
              return r
                ? resizedUrl(safeUrl(r.startsWith("http") ? r : `https:${r}`))
                : null;
            })
            .filter(Boolean)
        : [];
    const disc = sale ? Math.round((1 - Number(sp) / Number(lp)) * 100) : 0;
    const handle =
      new URL(safeUrl(url), location.origin).pathname
        .split("/products/")[1]
        ?.split("/")[0] ?? "";

    return `<div class="product-card">
      <div class="product-card__media">
        <a class="product-card__img-link" href="${escA(url)}"${ticket ? ` data-voyado-ticket="${escA(ticket)}"` : ""}${galleryUrls.length > 1 ? ` data-gallery="${escA(JSON.stringify(galleryUrls))}" data-gallery-index="0"` : ""} tabindex="-1" aria-hidden="true">
          ${
            img
              ? `<img class="product-card__img" src="${escA(img)}" alt="${escA(title)}" loading="${i < 4 ? "eager" : "lazy"}" decoding="${i === 0 ? "sync" : "async"}"${i === 0 ? ' fetchpriority="high"' : ""}>${isSwap && imgHover ? `<img class="product-card__img--hover" src="${escA(imgHover)}" alt="" aria-hidden="true">` : ""}${galleryUrls.length > 1 ? `<button class="product-card__gallery-prev" type="button" tabindex="-1" aria-label="Forrige bilde">${PREV_SVG}</button><button class="product-card__gallery-next" type="button" tabindex="-1" aria-label="Neste bilde">${NEXT_SVG}</button>` : ""}`
              : '<div class="product-card__img-placeholder"></div>'
          }
        </a>
        ${renderBadges(p, disc, sale)}
      </div>
      <div class="product-card__details">
        <div class="product-card__info-row">
          <div class="product-card__text">
            <div class="product-card__names">
              ${vendor ? `<p class="product-card__vendor">${esc(vendor)}</p>` : ""}
              <a class="product-card__title-link" href="${escA(url)}"${ticket ? ` data-voyado-ticket="${escA(ticket)}"` : ""}>
                <p class="product-card__title">${esc(title)}</p>
              </a>
            </div>
            <div class="product-card__prices">
              ${
                sale
                  ? `<p class="product-card__price product-card__price--sale">${fmt(sp)}</p><p class="product-card__compare">${fmt(lp)}</p>`
                  : sp !== null
                    ? `<p class="product-card__price">${fmt(sp)}</p>`
                    : ""
              }
            </div>
          </div>
          <quick-buy data-product-url="/products/${escA(handle)}" data-has-sizes="false" data-has-colors="false" data-color-option="0" data-size-option="0" data-first-color="">
            <form action="/cart/add" method="post" class="product-card__atc-form">
              <input type="hidden" name="id" value="">
              <input type="hidden" name="quantity" value="1">
              <button class="product-card__atc" type="submit" aria-label="Legg i handlevogn" data-variant-id=""${atcVariantTicket ? ` data-voyado-variant-ticket="${escA(atcVariantTicket)}"` : ""}${!p.inStock ? " disabled" : ""}>${PLUS_SVG}</button>
            </form>
          </quick-buy>
        </div>
      </div>
    </div>`;
  }

  // The product-page query also returns the primary product (`productGroup`)
  // with a per-variant add-to-cart `ticket`. The slider only renders the
  // recommendation lists, but the PDP add-to-cart needs those tickets — so we
  // expose a SKU→ticket lookup that ProductForm (product.js) reads when the
  // user adds the selected variant to cart. Built once per page: every
  // product-page slider echoes the same primary product, so the first wins.
  function captureVariantTickets(data) {
    if (window.voyadoVariantTicketBySku) return;

    const map = new Map();

    // Primary source: the top-level `tickets` dict the product-page query returns for
    // the VIEWED product — a flat { variantKey: ticket } map carrying PRODUCT_PAGE/
    // PRODUCT_INFO context (the correct ticket for a PDP add-to-cart). Elevate returns
    // this instead of a full `productGroup` when product details aren't requested,
    // which is our case — we only request recommendation lists. The variant keys are
    // the Shopify variant SKUs in our feed, so ProductForm can look them up by SKU.
    // (The per-variant tickets inside recommendationLists carry REC_LIST context and
    // are for adding a *recommended* product — not the viewed one — so we ignore them.)
    if (data.tickets && typeof data.tickets === "object") {
      for (const [key, ticket] of Object.entries(data.tickets)) {
        if (key && ticket) map.set(String(key), ticket);
      }
    }

    // Fallback: if a future query DOES request product details, the viewed product
    // comes back as `productGroup` with per-variant tickets instead of the flat dict.
    if (!map.size) {
      const main = data.productGroup?.products?.[0] ?? data.productGroup;
      const variants = main?.variants;
      if (Array.isArray(variants)) {
        for (const v of variants) {
          const sku = v.sku ?? v.key;
          if (sku && v.ticket) map.set(String(sku), v.ticket);
        }
      }
    }

    if (!map.size) {
      console.warn("[Voyado ATC] product-page response had no variant tickets", data);
      return;
    }
    window.voyadoVariantTicketBySku = (sku) =>
      sku == null ? null : map.get(String(sku)) ?? null;
  }

  // notify.click sends with keepalive internally (survives link navigation)
  // and auto-attaches market + customerKey/sessionKey.
  function sendClick(ticket) {
    if (!ticket || !window.elevate?.notify) return;
    window.elevate.notify.click(ticket);
  }

  function setupSlider(grid, refs, signal) {
    window.dragScroll?.(grid, signal);
    const navUpdate = window.swiperNav?.(grid, { prev: refs.prev, next: refs.next }, signal);

    let raf = null;
    const updateScrollbar = () => {
      const wrap = refs.scrollbarWrap, thumb = refs.scrollbarThumb;
      if (!wrap || !thumb) return;
      const { scrollLeft, scrollWidth, clientWidth } = grid;
      const overflowing = scrollWidth - clientWidth > 1;
      wrap.hidden = !overflowing;
      if (!overflowing) return;
      thumb.style.width = `${(clientWidth / scrollWidth) * 100}%`;
      thumb.style.left = `${(scrollLeft / scrollWidth) * 100}%`;
    };
    if (refs.scrollbarWrap && refs.scrollbarThumb) {
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => { updateScrollbar(); raf = null; });
      };
      grid.addEventListener("scroll", onScroll, { passive: true, signal });
      window.addEventListener("resize", onScroll, { passive: true, signal });
    }

    // ATC — store variant ticket before form submits (capture: true)
    grid.addEventListener("click", (e) => {
      const btn = e.target.closest(".product-card__atc[data-voyado-variant-ticket]");
      if (!btn) return;
      try { sessionStorage.setItem("voyado_pending_atc_ticket", btn.dataset.voyadoVariantTicket); } catch {}
    }, { capture: true, signal });

    // Gallery prev/next (capture: true)
    grid.addEventListener("click", (e) => {
      const btn = e.target.closest(
        ".product-card__gallery-prev, .product-card__gallery-next"
      );
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const link = btn.closest("[data-gallery]");
      if (!link) return;
      const imgs = JSON.parse(link.dataset.gallery);
      const dir = btn.classList.contains("product-card__gallery-next") ? 1 : -1;
      const cur = Number(link.dataset.galleryIndex ?? 0);
      const next = (cur + dir + imgs.length) % imgs.length;
      link.dataset.galleryIndex = next;
      const imgEl = link.querySelector(".product-card__img");
      if (imgEl) imgEl.src = imgs[next];
    }, { capture: true, signal });

    // Click-tracking (capture: true)
    grid.addEventListener("click", (e) => {
      const link = e.target.closest("a[data-voyado-ticket]");
      if (link) sendClick(link.dataset.voyadoTicket);
    }, { capture: true, signal });

    return { update() { navUpdate?.(); updateScrollbar(); } };
  }

  window.VoyadoRec = {
    PLUS_SVG, PREV_SVG, NEXT_SVG, NEEDS_PRODUCT,
    fmt, esc, escA, safeUrl,
    renderBadges, renderCard,
    captureVariantTickets, sendClick,
    setupSlider,
  };
})();
